import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicDataService } from '../../services/public-data.service';
import { LogService } from '../../services/log.service';
import { ContentSettings } from '../../models/settings.model';

type ModalContent = {
  title: string;
  content: string;
} | null;

@Component({
  selector: 'app-footer',
  standalone: true,
  template: `
<footer class="border-t border-slate-800 bg-slate-950 text-slate-300">
  <div class="w-full px-6 py-14 lg:px-10">
    <div class="mx-auto w-full max-w-[1400px]">
      <div class="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-4">
        <div class="xl:col-span-2">
          <div class="flex items-center gap-3">
            <span class="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-indigo-900/30">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none">
                <path d="M16.5 7.5A6.5 6.5 0 1 0 16.5 16.5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
                <path d="M10 14.5L14.5 10" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </span>
            <div>
              <h2 class="text-xl font-black tracking-tight text-white">Correct<span class="text-blue-400">ify</span></h2>
              <p class="text-[11px] uppercase tracking-[0.2em] text-blue-300/70">Performance em Redação</p>
            </div>
          </div>
          <p class="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">
            Plataforma profissional para evolução em redação, com diagnóstico técnico, histórico de desempenho
            e plano de ação orientado por critério de banca.
          </p>
        </div>

        <div>
          <h3 class="text-sm font-bold uppercase tracking-wider text-slate-200">Navegação</h3>
          <div class="mt-4 flex flex-col gap-2 text-sm">
            <a href="#plans" class="text-slate-400 transition-colors hover:text-white">Planos</a>
            <button (click)="openModal('terms')" class="text-left text-slate-400 transition-colors hover:text-white">Termos de Uso</button>
            <button (click)="openModal('privacy')" class="text-left text-slate-400 transition-colors hover:text-white">Política de Privacidade</button>
          </div>
        </div>

        <div>
          <h3 class="text-sm font-bold uppercase tracking-wider text-slate-200">Contato</h3>
          <div class="mt-4 flex flex-col gap-2 text-sm">
            <a href="mailto:contato@usecorrectify.com" class="text-slate-400 transition-colors hover:text-white">contato&#64;usecorrectify.com</a>
            <span class="text-slate-500">Canal de atendimento e LGPD</span>
          </div>
        </div>
      </div>

      <div class="mt-10 border-t border-slate-800 pt-6 text-xs text-slate-500">
        (c) 2026 Correctify. Todos os direitos reservados.
      </div>
    </div>
  </div>
</footer>

@if (modalContent(); as modal) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" (click)="closeModal()">
    <div class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white" (click)="$event.stopPropagation()">
      <div class="flex items-center justify-between border-b border-slate-200 p-6">
        <h2 class="text-2xl font-bold text-slate-900">{{ modal.title }}</h2>
        <button (click)="closeModal()" class="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="overflow-y-auto p-6">
        <div class="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{{ modal.content }}</div>
      </div>
    </div>
  </div>
}
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class FooterComponent {
  private publicDataService = inject(PublicDataService);
  private logService = inject(LogService);

  readonly modalContent = signal<ModalContent>(null);
  private contentSettings: ContentSettings | null = null;

  async openModal(type: 'terms' | 'privacy') {
    if (!this.contentSettings) {
      try {
        this.contentSettings = await this.publicDataService.getSettingsContent();
      } catch (e) {
        await this.logService.logError('Footer failed to load legal content', e);
        this.modalContent.set({
          title: 'Erro',
          content: 'Não foi possível carregar o conteúdo no momento. Tente novamente.',
        });
        return;
      }
    }

    if (type === 'terms') {
      this.modalContent.set({
        title: 'Termos de Uso',
        content: this.contentSettings.termsOfUse,
      });
      return;
    }

    this.modalContent.set({
      title: 'Política de Privacidade',
      content: this.contentSettings.privacyPolicy,
    });
  }

  closeModal() {
    this.modalContent.set(null);
  }
}
